import React, { useState, useEffect } from 'react';
import { SafeAreaView, View, Text, StyleSheet, TouchableOpacity, Dimensions, ScrollView, RefreshControl, StatusBar, Alert, Modal, FlatList, ActivityIndicator } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

const { width } = Dimensions.get('window');
const PRIMARY = '#0e4e96';
const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const api = axios.create({ baseURL: API_BASE_URL, timeout: 15000, headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-API-Key': API_KEY } });
api.interceptors.request.use(async c => { try { const t = await AsyncStorage.getItem('auth_token')||await AsyncStorage.getItem('token'); if(t)c.headers.Authorization=`Bearer ${t}`; }catch{} return c; });
const EP = { companyProfile:(id)=>`/api/company-profile/${id}`, deals:'/api/data/crm_deals', contacts:'/api/data/crm_contacts', activities:'/api/data/crm_activities', stages:'/api/data/crm_stages' };
const getBusinessId=async()=>{try{return await AsyncStorage.getItem('company_id')||await AsyncStorage.getItem('company_id')||'0';}catch{return '0';}};
const safeData=r=>(r?.data?.success&&Array.isArray(r.data.data))?r.data.data:[];

export default function OpportunityTrackingScreen({ navigation, route }) {
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('pipeline');
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filterStage, setFilterStage] = useState('all');
  const [companyId, setCompanyId] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [stages, setStages] = useState([]);
  const [activities, setActivities] = useState([]);
  const [analytics, setAnalytics] = useState({ totalValue:0, weightedValue:0, avgDealSize:0, avgCycleTime:0, winRate:0, stageConversion:{} });

  useEffect(()=>{init();},[]);
  const init=async()=>{try{setIsLoading(true);const b=route?.params?.company_id||await getBusinessId();setCompanyId(b);await fetchAll(b);}catch(e){console.error(e);}finally{setIsLoading(false);}};
  const onRefresh=async()=>{setRefreshing(true);await init();setRefreshing(false);};

  const fetchAll=async(bizId)=>{
    const cid=parseInt(bizId)||0;const p={company_id:cid};
    const [profileRes,dealsRes,contactsRes,activitiesRes,stagesRes]=await Promise.allSettled([
      api.get(EP.companyProfile(cid)),api.get(EP.deals,{params:p}),api.get(EP.contacts,{params:p}),
      api.get(EP.activities,{params:p}),api.get(EP.stages),
    ]);
    const profile=profileRes.status==='fulfilled'?(profileRes.value?.data?.success?(Array.isArray(profileRes.value.data.data)?profileRes.value.data.data[0]:profileRes.value.data.data):null):null;
    if(profile){setCompanyProfile(profile);await AsyncStorage.setItem('company_profile',JSON.stringify(profile));}
    else{try{const c=await AsyncStorage.getItem('company_profile');if(c)setCompanyProfile(JSON.parse(c));}catch{}}

    const deals=safeData(dealsRes.status==='fulfilled'?dealsRes.value:null);
    const acts=safeData(activitiesRes.status==='fulfilled'?activitiesRes.value:null);
    const stgs=safeData(stagesRes.status==='fulfilled'?stagesRes.value:null);
    setActivities(acts);
    setStages(stgs.length>0?stgs:[{stage_id:'lead',name:'Lead',color:'#94a3b8',probability_weight:10},{stage_id:'contacted',name:'Contacted',color:'#3b82f6',probability_weight:25},{stage_id:'qualified',name:'Qualified',color:'#f59e0b',probability_weight:50},{stage_id:'proposal',name:'Proposal',color:'#8b5cf6',probability_weight:65},{stage_id:'negotiation',name:'Negotiation',color:'#ec4899',probability_weight:80},{stage_id:'closed_won',name:'Won',color:'#10b981',probability_weight:100},{stage_id:'closed_lost',name:'Lost',color:'#ef4444',probability_weight:0}]);

    const opps=deals.map(d=>({
      id:String(d.id),name:d.contact_name||'Unknown',company:d.company||'',email:d.contact_email||'',
      stage:d.stage||'lead',value:parseFloat(d.value||0),probability:d.probability||0,
      priority:d.priority||'medium',source:d.source||'direct',notes:d.notes||'',
      createdAt:d.created_at,updatedAt:d.updated_at,dealId:d.id,
      daysInStage:Math.max(1,Math.floor((Date.now()-new Date(d.updated_at||d.created_at).getTime())/(1000*60*60*24))),
      nextAction:d.next_action||'Follow up',dueDate:d.due_date,
    }));
    setOpportunities(opps);

    // Analytics
    const won=opps.filter(o=>o.stage==='closed_won');
    const open=opps.filter(o=>!['closed_won','closed_lost'].includes(o.stage));
    const totalVal=opps.reduce((s,o)=>s+o.value,0);
    const weightedVal=opps.reduce((s,o)=>s+o.value*(o.probability/100),0);
    const stageConv={};
    const stgIds=(stgs.length>0?stgs:[]).map(s2=>s2.stage_id);
    stgIds.forEach(sid=>{const cnt=opps.filter(o=>o.stage===sid).length;stageConv[sid]=cnt;});
    setAnalytics({totalValue:totalVal,weightedValue:weightedVal,avgDealSize:opps.length>0?totalVal/opps.length:0,
      avgCycleTime:opps.length>0?Math.round(opps.reduce((s,o)=>s+o.daysInStage,0)/opps.length):0,
      winRate:opps.length>0?(won.length/opps.length*100):0,stageConversion:stageConv});
  };

  const formatCurrency=v=>new Intl.NumberFormat('en-GB',{style:'currency',currency:companyProfile?.currency||'GBP',minimumFractionDigits:0,maximumFractionDigits:0}).format(v||0);
  const formatDate=d=>d?new Date(d).toLocaleDateString('en-GB',{day:'numeric',month:'short'}):'';
  const getStageColor=s=>({lead:'#6B7280',contacted:'#3B82F6',qualified:'#F59E0B',proposal:'#8B5CF6',negotiation:'#EC4899',closed_won:'#10B981',closed_lost:'#EF4444'}[s]||'#6B7280');

  const filtered=filterStage==='all'?opportunities:opportunities.filter(o=>o.stage===filterStage);

  const renderHeader=()=>(
    <LinearGradient colors={['#1E40AF','#3B82F6']} style={st.header}>
      <View style={st.headerRow}>
        <TouchableOpacity onPress={()=>navigation?.goBack()} style={st.hBtn}><MaterialCommunityIcons name="arrow-left" size={24} color="#FFF"/></TouchableOpacity>
        <View style={{flex:1,marginLeft:12}}><Text style={st.hTitle}>{companyProfile?.name||'Opportunity Tracking'}</Text>
          <Text style={st.hSub}>{opportunities.length} tracked · {formatCurrency(analytics.weightedValue)} weighted</Text></View>
        <TouchableOpacity onPress={()=>navigation?.navigate?.('OpportunityScreen')} style={st.hBtn}><MaterialCommunityIcons name="plus" size={24} color="#FFF"/></TouchableOpacity>
      </View>
    </LinearGradient>
  );

  const renderSummary=()=>(
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={st.statsBar} contentContainerStyle={{paddingHorizontal:16}}>
      {[{l:'Pipeline',v:formatCurrency(analytics.totalValue),c:'#1E40AF'},{l:'Weighted',v:formatCurrency(analytics.weightedValue),c:'#10B981'},
        {l:'Avg Deal',v:formatCurrency(analytics.avgDealSize),c:'#F59E0B'},{l:'Avg Days',v:String(analytics.avgCycleTime),c:'#8B5CF6'},
        {l:'Win Rate',v:`${analytics.winRate.toFixed(1)}%`,c:'#3B82F6'}].map((s,i)=>(
        <View key={i} style={st.statCard}><Text style={[st.statVal,{color:s.c}]}>{s.v}</Text><Text style={st.statLbl}>{s.l}</Text></View>
      ))}
    </ScrollView>
  );

  const renderTabs=()=>(
    <View style={st.tabsRow}><ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{paddingHorizontal:12}}>
      {[{k:'pipeline',t:'Pipeline View',i:'pipe'},{k:'timeline',t:'Timeline',i:'timeline-clock'},{k:'activities',t:'Activities',i:'history'},{k:'funnel',t:'Funnel',i:'filter-variant'}].map(tab=>(
        <TouchableOpacity key={tab.k} style={[st.tab,activeTab===tab.k&&st.activeTab]} onPress={()=>setActiveTab(tab.k)}>
          <MaterialCommunityIcons name={tab.i} size={18} color={activeTab===tab.k?'#1E40AF':'#6B7280'}/>
          <Text style={[st.tabTxt,activeTab===tab.k&&{color:'#1E40AF',fontWeight:'600'}]}>{tab.t}</Text>
        </TouchableOpacity>
      ))}
    </ScrollView></View>
  );

  const renderPipelineView=()=>(
    <View>{stages.map((stg,idx)=>{
      const stgOpps=opportunities.filter(o=>o.stage===stg.stage_id);
      const stgVal=stgOpps.reduce((s,o)=>s+o.value,0);
      if(stgOpps.length===0)return null;
      return (
        <View key={idx} style={st.section}>
          <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
            <View style={{flexDirection:'row',alignItems:'center'}}>
              <View style={[st.dot,{backgroundColor:stg.color||'#6B7280'}]}/>
              <Text style={st.secTitle}>{stg.name}</Text>
              <View style={[st.badge,{backgroundColor:(stg.color||'#6B7280')+'20',marginLeft:8}]}><Text style={[st.badgeTxt,{color:stg.color}]}>{stgOpps.length}</Text></View>
            </View>
            <Text style={{fontSize:14,fontWeight:'700',color:'#10B981'}}>{formatCurrency(stgVal)}</Text>
          </View>
          {stgOpps.map(o=>(
            <TouchableOpacity key={o.id} style={st.oppCard} onPress={()=>{setSelected(o);setShowModal(true);}}>
              <View style={{flexDirection:'row',justifyContent:'space-between'}}>
                <View style={{flex:1}}><Text style={st.oppName}>{o.name}</Text><Text style={st.oppMeta}>{o.company} · {o.priority}</Text></View>
                <View style={{alignItems:'flex-end'}}>{o.value>0&&<Text style={st.oppVal}>{formatCurrency(o.value)}</Text>}
                  <Text style={st.oppDays}>{o.daysInStage}d in stage</Text></View>
              </View>
              <View style={st.probBar}><View style={[st.probFill,{width:`${o.probability}%`,backgroundColor:o.probability>50?'#10B981':o.probability>25?'#F59E0B':'#EF4444'}]}/></View>
            </TouchableOpacity>
          ))}
        </View>
      );
    })}</View>
  );

  const renderTimeline=()=>(
    <View style={st.section}><Text style={st.secTitle}>Recent Activity Timeline</Text>
      {activities.length===0?<View style={st.empty}><MaterialCommunityIcons name="timeline-clock-outline" size={48} color="#D1D5DB"/><Text style={st.emptyTxt}>No activities recorded</Text></View>:
        activities.slice(0,20).map((a,i)=>{
          const icons={call:'phone',email:'email',meeting:'account-group',note:'note',task:'checkbox-marked'};
          const colors={call:'#3B82F6',email:'#F59E0B',meeting:'#8B5CF6',note:'#6B7280',task:'#10B981'};
          return (
            <View key={i} style={st.timelineItem}>
              <View style={st.timelineDot}><View style={[st.dotInner,{backgroundColor:colors[a.type]||'#6B7280'}]}><MaterialCommunityIcons name={icons[a.type]||'circle'} size={14} color="#FFF"/></View>
                {i<activities.length-1&&<View style={st.timelineLine}/>}</View>
              <View style={st.timelineContent}>
                <Text style={st.tlTitle}>{a.title}</Text>
                {a.description&&<Text style={st.tlDesc}>{a.description}</Text>}
                <Text style={st.tlMeta}>{a.type} · {a.duration?`${a.duration}min · `:''}{ formatDate(a.scheduled_at||a.created_at)}</Text>
              </View>
              {a.completed&&<MaterialCommunityIcons name="check-circle" size={18} color="#10B981"/>}
            </View>
          );
        })
      }
    </View>
  );

  const renderFunnel=()=>{
    const stageOrder=['lead','contacted','qualified','proposal','negotiation','closed_won'];
    const funnelData=stageOrder.map(sid=>{
      const cnt=opportunities.filter(o=>o.stage===sid).length;
      const val=opportunities.filter(o=>o.stage===sid).reduce((s,o)=>s+o.value,0);
      const stg=stages.find(s=>s.stage_id===sid);
      return {name:stg?.name||sid,count:cnt,value:val,color:stg?.color||getStageColor(sid)};
    }).filter(f=>f.count>0);
    const maxCount=Math.max(...funnelData.map(f=>f.count),1);
    return (
      <View style={st.section}><Text style={st.secTitle}>Sales Funnel</Text>
        {funnelData.length===0?<View style={st.empty}><Text style={st.emptyTxt}>No funnel data</Text></View>:
          funnelData.map((f,i)=>(
            <View key={i} style={st.funnelRow}>
              <View style={[st.funnelBar,{width:`${(f.count/maxCount)*100}%`,backgroundColor:f.color}]}/>
              <View style={st.funnelInfo}>
                <Text style={st.funnelName}>{f.name} ({f.count})</Text>
                <Text style={st.funnelVal}>{formatCurrency(f.value)}</Text>
              </View>
            </View>
          ))
        }
      </View>
    );
  };

  const renderContent=()=>{
    const wrap=ch=><ScrollView style={{flex:1}} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#1E40AF']}/>} showsVerticalScrollIndicator={false}>{ch}<View style={{height:40}}/></ScrollView>;
    switch(activeTab){case 'pipeline':return wrap(renderPipelineView());case 'timeline':return wrap(renderTimeline());case 'activities':return wrap(renderTimeline());case 'funnel':return wrap(renderFunnel());default:return null;}
  };

  const renderModal=()=>(
    <Modal visible={showModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={()=>setShowModal(false)}>
      <View style={st.modal}><View style={st.modalH}><Text style={st.modalTitle}>{selected?.name||'Details'}</Text>
        <TouchableOpacity style={st.modalClose} onPress={()=>setShowModal(false)}><MaterialCommunityIcons name="close" size={24} color="#6B7280"/></TouchableOpacity></View>
      {selected&&<ScrollView style={{flex:1,padding:16}}>
        <View style={st.mSec}><Text style={st.mSecTitle}>Opportunity</Text>
          <Text style={st.mTxt}>Company: {selected.company}</Text><Text style={st.mTxt}>Contact: {selected.name}</Text>
          <Text style={st.mTxt}>Stage: {selected.stage}</Text><Text style={st.mTxt}>Value: {formatCurrency(selected.value)}</Text>
          <Text style={st.mTxt}>Probability: {selected.probability}%</Text><Text style={st.mTxt}>Days in Stage: {selected.daysInStage}</Text>
          <Text style={st.mTxt}>Priority: {selected.priority}</Text><Text style={st.mTxt}>Source: {selected.source}</Text>
        </View>
        {companyProfile&&<View style={st.mSec}><Text style={st.mSecTitle}>Your Company</Text><Text style={st.mTxt}>{companyProfile.name}{companyProfile.industry?` · ${companyProfile.industry}`:''}</Text></View>}
      </ScrollView>}</View>
    </Modal>
  );

  if(isLoading)return <SafeAreaView style={st.loading}><StatusBar barStyle="dark-content"/><ActivityIndicator size="large" color="#1E40AF"/><Text style={st.loadingTxt}>Tracking opportunities...</Text></SafeAreaView>;

  return (
    <SafeAreaView style={st.container}><StatusBar barStyle="light-content"/>
      {renderHeader()}{renderSummary()}{renderTabs()}{renderContent()}{renderModal()}
    </SafeAreaView>
  );
}

const st=StyleSheet.create({
  container:{flex:1,backgroundColor:'#F3F4F6'},loading:{flex:1,justifyContent:'center',alignItems:'center',backgroundColor:'#F3F4F6'},loadingTxt:{fontSize:16,color:'#6B7280',marginTop:16},
  header:{paddingTop:8,paddingBottom:16,paddingHorizontal:16},headerRow:{flexDirection:'row',alignItems:'center'},hBtn:{width:40,height:40,borderRadius:20,backgroundColor:'rgba(255,255,255,0.12)',justifyContent:'center',alignItems:'center'},hTitle:{fontSize:20,fontWeight:'bold',color:'#FFF'},hSub:{fontSize:13,color:'#BFDBFE',marginTop:2},
  statsBar:{flexGrow:0,flexShrink:0,backgroundColor:'#FFF',paddingVertical:12,borderBottomWidth:1,borderBottomColor:'#E5E7EB'},statCard:{alignItems:'center',marginRight:16,minWidth:70},statVal:{fontSize:16,fontWeight:'bold'},statLbl:{fontSize:11,color:'#6B7280',marginTop:2},
  tabsRow:{backgroundColor:'#FFF',borderBottomWidth:1,borderBottomColor:'#E5E7EB'},tab:{flexDirection:'row',alignItems:'center',paddingHorizontal:14,paddingVertical:10,marginRight:4,borderRadius:20},activeTab:{backgroundColor:'#EFF6FF'},tabTxt:{fontSize:13,color:'#6B7280',marginLeft:6,fontWeight:'500'},
  section:{backgroundColor:'#FFF',margin:16,padding:16,borderRadius:12,borderWidth:1,borderColor:'#E5E7EB'},secTitle:{fontSize:17,fontWeight:'bold',color:'#1F2937',marginBottom:0,marginLeft:8},
  dot:{width:10,height:10,borderRadius:5},badge:{borderRadius:10,paddingHorizontal:8,paddingVertical:2},badgeTxt:{fontSize:11,fontWeight:'600'},
  oppCard:{backgroundColor:'#F9FAFB',borderRadius:8,padding:12,marginBottom:6},oppName:{fontSize:14,fontWeight:'600',color:'#1F2937'},oppMeta:{fontSize:12,color:'#6B7280',marginTop:1},oppVal:{fontSize:13,fontWeight:'700',color:'#10B981'},oppDays:{fontSize:11,color:'#9CA3AF',marginTop:2},
  probBar:{height:3,backgroundColor:'#E5E7EB',borderRadius:2,marginTop:6,overflow:'hidden'},probFill:{height:'100%',borderRadius:2},
  empty:{alignItems:'center',padding:32},emptyTxt:{fontSize:16,color:'#6B7280',marginTop:12},
  timelineItem:{flexDirection:'row',marginBottom:4},timelineDot:{alignItems:'center',width:30},dotInner:{width:26,height:26,borderRadius:13,justifyContent:'center',alignItems:'center'},timelineLine:{width:2,flex:1,backgroundColor:'#E5E7EB',marginTop:4},
  timelineContent:{flex:1,marginLeft:12,paddingBottom:16,borderBottomWidth:1,borderBottomColor:'#F3F4F6'},tlTitle:{fontSize:14,fontWeight:'600',color:'#1F2937'},tlDesc:{fontSize:12,color:'#6B7280',marginTop:1},tlMeta:{fontSize:11,color:'#9CA3AF',marginTop:2},
  funnelRow:{marginBottom:12},funnelBar:{height:32,borderRadius:6,justifyContent:'center',paddingLeft:12},funnelInfo:{flexDirection:'row',justifyContent:'space-between',marginTop:4,paddingHorizontal:4},funnelName:{fontSize:13,color:'#1F2937',fontWeight:'500'},funnelVal:{fontSize:13,fontWeight:'600',color:'#10B981'},
  modal:{flex:1,backgroundColor:'#FFF'},modalH:{flexDirection:'row',alignItems:'center',justifyContent:'space-between',padding:16,borderBottomWidth:1,borderBottomColor:'#E5E7EB'},modalTitle:{fontSize:18,fontWeight:'bold',color:'#1F2937',flex:1},modalClose:{width:40,height:40,borderRadius:20,backgroundColor:'#F3F4F6',justifyContent:'center',alignItems:'center'},
  mSec:{marginBottom:20},mSecTitle:{fontSize:16,fontWeight:'600',color:'#1F2937',marginBottom:8},mTxt:{fontSize:14,color:'#6B7280',lineHeight:22},
});